import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { MessageProcessor } from "@a2ui/web_core/v0_9";
import { analyticsChatCatalog } from "./catalog/analyticsChatCatalog";
import { postAnalyticsChatTurn, streamAnalyticsChatTurn } from "./analyticsChatApi";
import ChatMessage, { AssistantAvatar } from "./ChatMessage";
import QuickGuide, { STARTER_PROMPTS } from "./QuickGuide";
import { Button, Form } from "react-bootstrap";
import "./AnalyticsChat.css";

// One turn = one question the user asked plus whatever the backend streamed
// back for it: narrative text, an accumulated A2UI surface (if it produced
// a chart), and/or a refusal note. Turns are appended, never replaced or
// removed (until "New conversation"), so earlier answers and charts stay in the feed.
const createPendingTurn = (question) => ({
  turnId: null,
  question,
  narrative: "",
  surfaceId: null,
  refusal: null,
  status: "sending", // sending -> streaming -> done | error | stopped
});

const GUIDE_SEEN_KEY = "analyticsChatGuideSeen";

// First visit opens the guide once; after that it is one click away in the header.
const isFirstVisit = () => {
  try {
    return !localStorage.getItem(GUIDE_SEEN_KEY);
  } catch (e) {
    return false;
  }
};

const newConversationId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : null;

// Chat page: each turn is a question bubble and an assistant reply bubble with
// its chart/table inline, plus a docked composer to keep the conversation going.
const AnalyticsChat = () => {
  const [turns, setTurns] = useState([]);
  const [draft, setDraft] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const [showGuide, setShowGuide] = useState(isFirstVisit);

  // One shared processor for the whole page: every turn's surface lives in
  // its `model.surfacesMap`, keyed by the surfaceId the backend assigns, so
  // surfaces from earlier turns are never touched by a later turn's
  // messages. Built once per mount (guarded so React StrictMode's double
  // invocation in dev doesn't construct it twice).
  const processorRef = useRef(null);
  if (!processorRef.current) {
    processorRef.current = new MessageProcessor([analyticsChatCatalog]);
  }
  const processor = processorRef.current;

  // Held until "New conversation" rotates it, and sent with every turn. The backend
  // contract allows conversationId to be null on the first turn and
  // assigned by the server instead - generating it client-side up front was
  // the simpler of the two valid options (see report to backend team).
  const conversationIdRef = useRef(newConversationId());
  const feedEndRef = useRef(null);
  // Bumped on "New conversation" so an in-flight send can tell its turn was discarded.
  const generationRef = useRef(0);

  const abortControllersRef = useRef(new Map());

  useEffect(() => {
    const controllers = abortControllersRef.current;
    return () => {
      controllers.forEach((controller) => controller.abort());
      controllers.clear();
    };
  }, []);

  const updateTurn = (localId, patch) => {
    setTurns((prev) =>
      prev.map((t) =>
        t.localId === localId ? { ...t, ...(typeof patch === "function" ? patch(t) : patch) } : t
      )
    );
  };

  const sendQuestion = async (rawQuestion) => {
    const question = rawQuestion.trim();
    if (!question || isBusy) return;

    const localId = `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setTurns((prev) => [...prev, { ...createPendingTurn(question), localId }]);
    setDraft("");
    setIsBusy(true);
    const generation = generationRef.current;

    try {
      const response = await postAnalyticsChatTurn(conversationIdRef.current, question);
      if (generation !== generationRef.current) return;
      const body = response?.data;

      if (!body?.success || !body?.data?.turnId) {
        const errorMessage = body?.error?.message || "Failed to submit the question.";
        toast.error(errorMessage);
        updateTurn(localId, { status: "error" });
        return;
      }

      const { turnId, conversationId } = body.data;
      if (conversationId) {
        conversationIdRef.current = conversationId;
      }
      updateTurn(localId, { turnId, status: "streaming" });

      const controller = new AbortController();
      abortControllersRef.current.set(turnId, controller);

      await streamAnalyticsChatTurn(
        turnId,
        {
          onChat: (delta) => {
            updateTurn(localId, (t) => ({ narrative: t.narrative + delta }));
          },
          onA2ui: (message) => {
            processor.processMessages([message]);
            if (message?.createSurface?.surfaceId) {
              updateTurn(localId, { surfaceId: message.createSurface.surfaceId });
            }
            if (message?.updateComponents || message?.updateDataModel) {
              // Nudge a re-render so a surface that only now gained its
              // first components (no separate createSurface tracked yet
              // client-side) is picked up. A2uiSurface itself stays
              // reactive to further updates once mounted.
              updateTurn(localId, (t) => ({ ...t }));
            }
          },
          onRefusal: (message) => {
            // No toast here: refuse is also how the assistant answers plain
            // conversational questions ("hi", "what can you do") now, not
            // just true refusals - popping a red error toast for a greeting
            // reply would be the wrong tone. The inline reply in the chat
            // (below) is enough either way.
            updateTurn(localId, { refusal: message });
          },
          onDone: () => {
            updateTurn(localId, { status: "done" });
            abortControllersRef.current.delete(turnId);
          },
          onError: (error) => {
            console.error("Analytics chat stream error:", error);
            toast.error("Lost connection to the analytics stream.");
            updateTurn(localId, { status: "error" });
            abortControllersRef.current.delete(turnId);
          },
        },
        controller.signal
      );
    } catch (error) {
      console.error("Analytics chat error:", error);
      toast.error("Failed to submit the question.");
      updateTurn(localId, { status: "error" });
    } finally {
      setIsBusy(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendQuestion(draft);
  };

  const handleNewConversation = () => {
    abortControllersRef.current.forEach((controller) => controller.abort());
    abortControllersRef.current.clear();
    conversationIdRef.current = newConversationId();
    generationRef.current += 1;
    setTurns([]);
    setDraft("");
    setIsBusy(false);
  };

  const closeGuide = () => {
    setShowGuide(false);
    try {
      localStorage.setItem(GUIDE_SEEN_KEY, "1");
    } catch (e) {
      // storage unavailable: the guide just opens again next visit
    }
  };

  const pickFromGuide = (question) => {
    closeGuide();
    sendQuestion(question);
  };

  // Lets the user bail out of a stuck/slow turn without reloading the page.
  // Aborting the fetch() is enough on its own - streamAnalyticsChatTurn
  // swallows AbortError silently (see analyticsChatApi.js) so it never
  // surfaces as an "error" - but nothing else would ever clear the
  // "streaming" spinner for a turn nobody is listening to anymore, so this
  // sets its status directly instead of waiting on a callback that won't come.
  const handleStop = (turn) => {
    const controller = turn.turnId ? abortControllersRef.current.get(turn.turnId) : null;
    controller?.abort();
    if (turn.turnId) {
      abortControllersRef.current.delete(turn.turnId);
    }
    updateTurn(turn.localId, { status: "stopped" });
  };

  const turnSurfaces = useMemo(() => {
    // Re-read from the processor's surface map on every render of the turn
    // list, since the processor's own state (not React state) owns surfaces.
    return turns.map((t) => (t.surfaceId ? processor.model.getSurface(t.surfaceId) : null));
  }, [turns, processor]);

  // Scroll once per new turn / status change, not on every streamed delta.
  const lastStatus = turns[turns.length - 1]?.status;
  useEffect(() => {
    feedEndRef.current?.scrollIntoView?.({ behavior: "smooth", block: "end" });
  }, [turns.length, lastStatus]);

  return (
    <div className="analytics-chat-page">
      <div className="analytics-chat-header">
        <div className="d-flex align-items-center gap-2">
          <AssistantAvatar />
          <h4 className="mb-0">Analytics Assistant</h4>
        </div>
        <div className="d-flex gap-2">
          <Button size="sm" variant="outline-secondary" onClick={() => setShowGuide(true)}>
            Guide
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={handleNewConversation}
            disabled={turns.length === 0}
          >
            New chat
          </Button>
        </div>
      </div>

      <div className="analytics-chat-feed">
        <div className="analytics-chat-feed-inner">
          {turns.length === 0 && (
            <div className="analytics-msg-ai">
              <AssistantAvatar />
              <div className="analytics-bubble analytics-bubble--ai">
                <p className="analytics-chat-narrative">
                  Hi, I can answer questions about your devices, homes, users and OTA campaigns,
                  with charts and tables right here in the chat. Try one of these, or open the
                  guide to see everything I can do.
                </p>
                <div className="d-flex flex-wrap gap-2">
                  {STARTER_PROMPTS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className="analytics-chip"
                      disabled={isBusy}
                      onClick={() => sendQuestion(p)}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {turns.map((turn, i) => (
            <ChatMessage key={turn.localId} turn={turn} surface={turnSurfaces[i]} onStop={handleStop} />
          ))}
          <div ref={feedEndRef} />
        </div>
      </div>

      <Form onSubmit={handleSubmit} className="analytics-composer">
        <Form.Control
          as="textarea"
          rows={1}
          placeholder="Ask about devices, homes, users... (Enter to send, Shift+Enter for a new line)"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              sendQuestion(draft);
            }
          }}
          disabled={isBusy}
        />
        <Button type="submit" disabled={isBusy || !draft.trim()}>
          Send
        </Button>
      </Form>

      <QuickGuide show={showGuide} onHide={closeGuide} onPick={pickFromGuide} disabled={isBusy} />
    </div>
  );
};

export default AnalyticsChat;
